import { useCompanyListQuery } from "Company/Api/Query/useCompanyListQuery";
import { CompanyPaths } from "Company/Routes/Routing";
import type { CompanyResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { Edit } from "@mui/icons-material";
import { Fab, Stack, Typography } from "@mui/material";
import { createMRTColumnHelper, type MRT_Row } from "material-react-table";
import { useNavigate } from "react-router";

type TableAction = {
	action: () => void;
	icon: JSX.Element;
};

type CompanyTableRow = CompanyResponseDto & {
	actions: TableAction[];
};

const companyTableResource = Resources.base.company.detail.info;
const companySharedResource = Resources.base.company.shared;

export const useCompaniesListTable = () => {
	const { t } = useResource();
	const columnHelper = createMRTColumnHelper<CompanyTableRow>();
	const { data, isLoading } = useCompanyListQuery();
	const navigate = useNavigate();
	const { formatDate } = useDateFormat();

	const tableData = (data ?? []).map((company) => ({
		...company,
		actions: [
			{
				action: () => navigate(CompanyPaths.Detail.path(company.id)),
				icon: <Edit />,
			},
		],
	}));

	const columns = [
		columnHelper.accessor((row) => row.id, {
			id: "id",
			header: t(companyTableResource.id),
			enableColumnActions: false,
			maxSize: 60,
			minSize: 0,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Typography variant="tableCell">{row.original.id}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.name, {
			id: "name",
			header: t(companyTableResource.name),
			enableColumnActions: false,
			maxSize: 100,
			minSize: 0,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Typography variant="tableCell">{row.original.name}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.email, {
			id: "email",
			header: t(companyTableResource.email),
			enableColumnActions: false,
			maxSize: 100,
			minSize: 0,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Typography variant="tableCell">{row.original.email}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.phone, {
			id: "phone",
			header: t(companyTableResource.phone),
			enableColumnActions: false,
			maxSize: 100,
			minSize: 0,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Typography variant="tableCell">{row.original.phone}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.country, {
			id: "country",
			header: t(companyTableResource.country),
			enableColumnActions: false,
			maxSize: 100,
			minSize: 0,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Typography variant="tableCell">{row.original.country}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.city, {
			id: "city",
			header: t(companyTableResource.city),
			enableColumnActions: false,
			maxSize: 100,
			minSize: 0,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Typography variant="tableCell">{row.original.city}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.businessId, {
			id: "businessId",
			header: t(companyTableResource.businessId),
			enableColumnActions: false,
			maxSize: 100,
			minSize: 0,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Typography variant="tableCell">{row.original.businessId}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.dateCreated, {
			id: "dateCreated",
			header: t(companyTableResource.dateCreated),
			enableColumnActions: false,
			maxSize: 100,
			minSize: 0,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Typography variant="tableCell">
					{formatDate(row.original.dateCreated)}
				</Typography>
			),
		}),
		{
			id: "actions",
			header: t(companySharedResource.actions),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<CompanyTableRow> }) => (
				<Stack direction="row" columnGap="4px">
					{row.original.actions?.map((action, index) => (
						<Fab
							key={index}
							onClick={action.action}
							size="small"
							sx={{
								background: "transparent",
								transition: "all 0.15s ease",
								"&:hover": { background: "transparent", opacity: 0.5 },
							}}
						>
							{action.icon}
						</Fab>
					))}
				</Stack>
			),
		},
	];

	const tableOptions = {
		...defaultTableConfig,
		columns,
		data: tableData,
		state: {
			showSkeletons: isLoading,
			isLoading,
		},
		initialState: {
			columnVisibility: {
				dateCreated: false,
			},
		},
		muiTableBodyRowProps: ({ row }) => ({
			onDoubleClick: () => {
				const companyId = row.original.id;
				navigate(CompanyPaths.Detail.path(companyId));
			},
		}),
	};

	return tableOptions;
};
